// ── CovenantForm ──
import { org } from "../../../shared/org";
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { fmtDate, now } from "../../../shared/format";
import { Badge, Btn, Confirm, FInput, FSelect, Field, Icon, Modal, ReadOnlyForm, Td, Th } from "../../../shared/ui";
import { useFreshForm } from "../../../shared/hooks";
import { ExceptionForm } from "../components/ExceptionForm";
import { ExceptionsTable } from "../components/ExceptionsTable";
import { NotesPanel } from "../components/NotesPanel";
import { CovenantCalendarForm } from "./CovenantCalendarForm";
import { useApp } from "../../../shared/appContext";

export const CovenantForm = ({ initial, accountid, accountFundingDate, accountMaturityDate, readOnly }) => {
    const { api, role, view, modal, setModal, closeModal, setConfirm, saving, error, saveRec, delRec } = useApp();
      const [f, set] = useFreshForm(initial || {
        accountid, covenantcatalogid:"", responsibleparty:"", covenantdirection:"",
        covenantfrequency:"", setdate:"", firsttestdate:"", daysaftertestdatedue:"",
        covenantrequirement:""
      }, "Covenant");
      const [catalog, setCatalog] = useState([]);
      const [genMsg, setGenMsg] = useState(null);
      const [generating, setGenerating] = useState(false);
      const [calendar, setCalendar] = useState([]);
      const [calEntry, setCalEntry] = useState(null);
      const [calRO, setCalRO] = useState(true);
      const [confirmGen, setConfirmGen] = useState(false);
      const [exceptions, setExceptions] = useState([]);   // NEW
      const [excEntry, setExcEntry] = useState(null);     // NEW: open exception
      const loadCalendar = () => {
      if (initial?.id) api.get("getCovenantCalendar?covenantid="+initial.id).then(setCalendar).catch(()=>setCalendar([]));
      };
      useEffect(()=>{ if (readOnly && initial?.id) loadCalendar(); },[readOnly, initial]);
      useEffect(()=>{
          if (readOnly && initial?.id)
            api.get("getCovenantCalendar?covenantid="+initial.id).then(setCalendar).catch(()=>setCalendar([]));
      },[readOnly, initial]);
      useEffect(()=>{
        api.get("getCovenantCatalog").then(setCatalog).catch(()=>setCatalog([]));
      },[]);
 
      const selectedCat = catalog.find(c => String(c.id) === String(f.covenantcatalogid));
      const isFieldExam = selectedCat?.name === "Field Exam";
 
      // NEW: pull exceptions from the most recent *completed* calendar entry
      const loadExceptions = () => {
        if (!(readOnly && initial?.id && isFieldExam)) { setExceptions([]); return; }
        const recent = [...calendar]
          .filter(e => e.status === "Completed" && e.completeddate)
          .sort((a,b) => (b.completeddate||"").localeCompare(a.completeddate||""))[0];
        if (recent) api.get("getCovenantExceptions?covenantcalendarid="+recent.id).then(setExceptions).catch(()=>setExceptions([]));
        else setExceptions([]);
      };
      useEffect(loadExceptions, [calendar, readOnly, initial, isFieldExam]);
 
      const catOptions = [{ value:"", label:"— Select Covenant —" }, ...catalog.map(c=>({ value:c.id, label:c.name }))];
      const firstDue = (()=>{
        if(!f.firsttestdate || f.daysaftertestdatedue===""||f.daysaftertestdatedue==null) return "";
        const d = new Date(f.firsttestdate.slice(0,10)+"T00:00:00Z");
        d.setUTCDate(d.getUTCDate()+Math.round(Number(f.daysaftertestdatedue)||0));
        return d.toISOString().slice(0,10);
      })();
 
      const computedEntries = (()=>{
        if(f.covenantfrequency==="Set Date") return 1;
        if(!accountFundingDate||!accountMaturityDate||!f.covenantfrequency) return null;
        const start = Math.max(new Date(accountFundingDate).getTime(), Date.now());
        const days = (new Date(accountMaturityDate).getTime() - start) / 86400000;
        const div = { Weekly:7, Monthly:30, Quarterly:90, "6-Months":180, Yearly:360 }[f.covenantfrequency];
        if(!div||days<=0) return null;
        return Math.max(1, Math.round(days/div));
       })();
 
      const handleGenerate = async () => {
        setGenerating(true); setGenMsg(null);
        try {
          const res = await api.post("generateCovenantCalendar", { covenantid: initial.id });
          setGenMsg("Created "+res.created + (res.skippedExisting?", "+res.skippedExisting+" already existed":"") + (res.skippedPast?", "+res.skippedPast+" past due skipped":""));
          loadCalendar();
          if (modal.onDone) modal.onDone();
        } catch(e){ setGenMsg("Error: "+e.message); }
        setGenerating(false);
      };
 
      const ST = ({ title }) => <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", marginTop:18, marginBottom:8, paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, gridColumn:"1/-1" }}>{title}</div>;
      const RO = ({ label, value }) => <Field label={label}><div style={{ padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #ddd", background:"#f5f5f5", color:"#555", minHeight:18 }}>{value||"—"}</div></Field>;
      const TA = { width:"100%", boxSizing:"border-box", padding:"7px 10px", fontSize:13, borderRadius:4, border:"1px solid #999", background:"#fff", color:"#1a1a1a", fontFamily:"inherit" };
 
      return <Modal title={readOnly?"View Covenant":initial?"Edit Covenant":"Add Covenant"} onClose={closeModal} wide
        onEdit={readOnly&&role.canEdit?()=>{ closeModal(); setModal({type:"covenant-edit",rec:initial,accountid,accountFundingDate,accountMaturityDate,onDone:modal.onDone}); }:null}>
        <ReadOnlyForm readOnly={readOnly}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:"0 16px" }}>
            <ST title="Covenant" />
            <Field label="Covenant Type"><FSelect value={f.covenantcatalogid||""} onChange={set("covenantcatalogid")} options={catOptions} /></Field>
            <Field label="Responsible Party"><FSelect value={f.responsibleparty||""} onChange={set("responsibleparty")} options={[{value:"",label:""},{value:"Aequum",label:org("short")},"Borrower"]} /></Field>
            <Field label="Direction"><FSelect value={f.covenantdirection||""} onChange={set("covenantdirection")} options={["",">=","<=","=",">"]} /></Field>
            <div />
            <RO label="Category"          value={selectedCat?.category} />
            <RO label="Frequency Guidance" value={selectedCat?.frequencydefaults} />
            <RO label="Due Date Guidance" value={selectedCat?.duedateguidance} />
            <RO label="Value Guidance"    value={selectedCat?.valueguidance} />
 
            <ST title="Schedule" />
            <Field label="Frequency"><FSelect value={f.covenantfrequency||""} onChange={set("covenantfrequency")} options={["","Weekly","Monthly","Quarterly","6-Months","Yearly","Set Date"]} /></Field>
            {f.covenantfrequency==="Set Date" && <Field label="Set Date"><FInput type="date" value={f.setdate?.slice(0,10)||""} onChange={set("setdate")} /></Field>}
            <Field label="First Test Date"><FInput type="date" value={f.firsttestdate?.slice(0,10)||""} onChange={set("firsttestdate")} /></Field>
            <Field label="Days After Test → Due"><FInput type="number" value={f.daysaftertestdatedue} onChange={set("daysaftertestdatedue")} /></Field>
            <RO label="First Due Date (calc)" value={firstDue?fmtDate(firstDue):""} />
            <RO label="Original Funding Date" value={accountFundingDate ? fmtDate(accountFundingDate) : "—"} />
            <RO label="Maturity Date" value={accountMaturityDate ? fmtDate(accountMaturityDate) : "—"} />
            <RO label="Estimated Entries to be created" value={computedEntries!=null ? String(computedEntries) : "—"} />
            <div />
 
            <ST title="Requirement" />
            <div style={{ gridColumn:"1/-1" }}><Field label="Covenant Requirement (carried into each calendar entry)"><textarea value={f.covenantrequirement||""} onChange={e=>set("covenantrequirement")(e.target.value)} rows={2} style={TA} /></Field></div>
          </div>
        </ReadOnlyForm>
            {initial?.id && <NotesPanel scope="record" entitytype="Covenant" entityid={initial.id} accountid={accountid} source="Covenant" />}
 
      {/* ===== Field Exam Exceptions (between Notes and Covenant Calendar) ===== */}
      {readOnly && initial?.id && isFieldExam && (
        <div style={{ marginTop:18 }}>
          <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, marginBottom:8 }}>
            Field Exam Exceptions ({exceptions.length})
          </div>
          <ExceptionsTable rows={exceptions} onRowClick={setExcEntry} />
        </div>
      )}
 
      {excEntry && <ExceptionForm initial={excEntry} onClose={()=>setExcEntry(null)} onSaved={loadExceptions} />}
 
      {readOnly && initial?.id && (
            <div style={{ marginTop:18 }}>
              <div style={{ fontSize:11, fontWeight:600, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em", paddingBottom:4, borderBottom:"1.5px solid "+B.cyanLight, marginBottom:8 }}>
                Covenant Calendar ({calendar.length})
              </div>
              {calendar.length===0
                ? <div style={{ fontSize:13, color:"var(--color-text-secondary)", padding:"4px 0" }}>No calendar entries yet.</div>
                : <div style={{ maxHeight:260, overflowY:"auto" }}>
                    <table style={{ width:"100%", borderCollapse:"collapse", whiteSpace:"nowrap" }}>
                <thead><tr><Th>Status</Th><Th>Test Date</Th><Th>Due Date</Th><Th>Completed</Th><Th>PM</Th><Th>Value</Th><Th></Th></tr></thead>
                <tbody>
                 {[...calendar].sort((a,b)=>(a.testdate||"").localeCompare(b.testdate||"")).map(e=>(
                    <tr key={e.id} style={{ cursor:"pointer" }} onClick={()=>{ setCalEntry(e); setCalRO(true); }}
                      onMouseEnter={ev=>ev.currentTarget.style.background="var(--color-background-secondary)"}
                      onMouseLeave={ev=>ev.currentTarget.style.background=""}>
                      <Td><Badge s={e.status||"—"} /></Td>
                      <Td>{fmtDate(e.testdate)}</Td>
                      <Td>{fmtDate(e.duedate)}</Td>
                      <Td>{e.completeddate?fmtDate(e.completeddate):"—"}</Td>
                      <Td>{e.portfoliomanagername||"—"}</Td>
                      <Td>{e.covenantvalue||"—"}</Td>
                      <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={ev=>ev.stopPropagation()}>
                        <Btn onClick={()=>{ setCalEntry(e); setCalRO(true); }}><Icon type="view" /></Btn>
                        {role.canEdit && <Btn onClick={()=>{ setCalEntry(e); setCalRO(false); }}><Icon type="edit" /></Btn>}
                        {role.canDelete && <Btn variant="danger" onClick={()=>setConfirm({ msg:"Delete this calendar entry?", onConfirm:()=>delRec("CovenantCalendar", e.id, "Deleted covenant calendar entry", loadCalendar) })}><Icon type="delete" /></Btn>}
                      </div></Td>
                    </tr>
                  ))}
                </tbody>
                    </table>
                  </div>}
            </div>
          )}
          {readOnly && initial?.id && <div style={{ marginTop:16, padding:"12px 14px", background:B.cyanLight, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
        <div style={{ fontSize:12, color:B.cyanDark }}>
          Generate calendar entries from this covenant. Additive only — existing and completed entries are never touched; past-due cycles are skipped.
          {genMsg && <div style={{ marginTop:4, fontWeight:600 }}>{genMsg}</div>}
        </div>
 
        <Btn variant="primary" disabled={generating || !f.covenantfrequency || (!f.firsttestdate && !f.setdate)}
        onClick={()=>setConfirmGen(true)}>
        {generating?"Generating...":"⚙ Generate Calendar"}
      </Btn>
      </div>}
 
        {!readOnly&&error&&<div style={{ color:"#993C1D",fontSize:12,marginBottom:8,marginTop:8 }}>{error}</div>}
        <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
          <Btn onClick={closeModal}>{readOnly?"Close":"Cancel"}</Btn>
          {!readOnly&&<Btn disabled={saving} onClick={()=>saveRec("saveCovenant",{...f,accountid:f.accountid||accountid,firstduedate:firstDue||null},initial?"Edited":"Added","Covenant",(initial?"Updated":"Added")+" covenant"+(selectedCat?": "+selectedCat.name:""),modal.onDone)} variant="primary">{saving?"Saving...":"Save"}</Btn>}
        </div>
        {confirmGen && (
        <Modal title="Confirm Calendar Generation" onClose={()=>setConfirmGen(false)}>
          <div style={{ fontSize:13, lineHeight:1.5, marginBottom:16 }}>
            You're about to run the Generate Calendar function for this covenant. New cycles will be created; existing and completed entries are left untouched, and past-due cycles are skipped.
          </div>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <Btn onClick={()=>setConfirmGen(false)}>Cancel</Btn>
            <Btn variant="primary" disabled={generating}
              onClick={async ()=>{ await handleGenerate(); setConfirmGen(false); }}>
              {generating?"Generating...":"Continue"}
            </Btn>
          </div>
        </Modal>
      )}
 
        {calEntry && <CovenantCalendarForm
        initial={calEntry}
        covenantid={initial.id}
        readOnly={calRO}
        onClose={()=>setCalEntry(null)}
        onSaved={loadCalendar}
        onRequestEdit={()=>setCalRO(false)} />}
      </Modal>;
    };
